import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  Elements,
  ExpressCheckoutElement,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import {
  Stripe,
  StripeElements,
  StripeElementsOptions,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementOptions,
  StripePaymentElementOptions,
} from "@stripe/stripe-js";
import {
  FormEvent,
  memo,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getStripePromise } from "../../../App";
import { stripeAppearance } from "../utils/stripeAppearance";
import { CreditCardIcon } from "../../icons/CreditCardIcon";

const CARD_FORM_REGION_ID = "express-checkout-card-form-region";

const EXPRESS_CHECKOUT_OPTIONS: StripeExpressCheckoutElementOptions = {
  layout: { maxColumns: 1, maxRows: 4, overflow: "auto" },
  buttonHeight: 48,
  paymentMethods: {
    applePay: "auto",
    googlePay: "auto",
    link: "auto",
    amazonPay: "auto",
    paypal: "never",
  },
};

const PAYMENT_ELEMENT_OPTIONS: StripePaymentElementOptions = {
  wallets: { applePay: "never", googlePay: "never" },
  // Stripe requires fields set to 'never' to be supplied at confirm time, so we
  // leave country visible rather than hiding it without a billing-details fallback.
  terms: { card: "never" },
};

export type ExpressCheckoutConfirmHandler = (params: {
  stripe: Stripe;
  elements: StripeElements;
}) => Promise<void>;

export type ExpressCheckoutWithCardFormProps = {
  elementsOptions: Omit<StripeElementsOptions, "appearance">;
  onConfirm: ExpressCheckoutConfirmHandler;
  formId?: string;
  submitButtonText: string;
  submitTestId?: string;
  onCancel?: () => void;
  cancelButtonText?: string;
  /** Optional terms/legal microcopy rendered below the card form. */
  terms?: ReactNode;
  disabled?: boolean;
  /**
   * Hide the form's internal "primary" submit button. Pass `true` when the
   * parent renders an external submit (e.g. a sticky bottom CTA wired via
   * `formId`) so users don't see two stacked Subscribe buttons.
   */
  hideInternalSubmitButton?: boolean;
  /**
   * Fires whenever the form's internal `submitting` flag flips. Lets the
   * parent (which owns the external sticky CTA) mirror the loading state
   * the moment the user clicks Submit — otherwise the external button
   * stays idle for the full Stripe confirm round-trip (~2-3s) before any
   * downstream "settling" signal lights it up.
   */
  onSubmittingChange?: (submitting: boolean) => void;
};

export const ExpressCheckoutWithCardForm = memo(
  (props: ExpressCheckoutWithCardFormProps) => {
    const options = useMemo<StripeElementsOptions>(
      () =>
        ({
          ...props.elementsOptions,
          appearance: stripeAppearance,
        }) as StripeElementsOptions,
      [props.elementsOptions],
    );
    const elementsKey = getElementsKey(props.elementsOptions);

    return (
      <Elements key={elementsKey} stripe={getStripePromise()} options={options}>
        <FormBody {...props} />
      </Elements>
    );
  },
);

function getElementsKey(
  elementsOptions: ExpressCheckoutWithCardFormProps["elementsOptions"],
) {
  if ("clientSecret" in elementsOptions && elementsOptions.clientSecret) {
    return elementsOptions.clientSecret;
  }

  return JSON.stringify(elementsOptions);
}

function FormBody({
  onConfirm,
  formId,
  submitButtonText,
  submitTestId,
  onCancel,
  cancelButtonText,
  terms,
  disabled = false,
  hideInternalSubmitButton = false,
  onSubmittingChange,
}: ExpressCheckoutWithCardFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [cardExpanded, setCardExpanded] = useState(false);
  const [walletsAvailable, setWalletsAvailable] = useState<boolean | null>(
    null,
  );
  const [paymentReady, setPaymentReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    onSubmittingChange?.(submitting);
  }, [submitting, onSubmittingChange]);

  const runConfirm = async (
    expressEvent?: StripeExpressCheckoutElementConfirmEvent,
  ) => {
    if (!stripe || !elements || submitting) {
      return;
    }
    setSubmitting(true);
    setError(undefined);
    try {
      const submitResult = await elements.submit();
      if (submitResult.error) {
        throw new Error(submitResult.error.message ?? "Something went wrong.");
      }
      await onConfirm({ stripe, elements });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      setError(msg);
      expressEvent?.paymentFailed({ reason: "fail" });
      setSubmitting(false);
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    void runConfirm();
  };

  const showWallets = !cardExpanded && walletsAvailable !== false;
  // Keep the card form mounted once it is the active fallback. Devices with no
  // wallet buttons report `walletsAvailable === false`, but still need the card
  // PaymentElement.
  const showDisclosure = cardExpanded || walletsAvailable !== false;

  return (
    <form
      id={formId}
      onSubmit={handleSubmit}
      style={{ width: "100%", maxWidth: "100%", minWidth: 0 }}
    >
      <VStack
        gap={4}
        alignItems="stretch"
        width="100%"
        maxWidth="100%"
        minWidth={0}
      >
        {showWallets ? (
          <Box
            position="relative"
            minHeight={walletsAvailable === null ? "120px" : undefined}
          >
            <ExpressCheckoutElement
              options={EXPRESS_CHECKOUT_OPTIONS}
              onReady={(event) => {
                // `availablePaymentMethods === undefined` is Stripe's signal that the element
                // won't render anything. A defined object means at least one wallet (including
                // Link/Amazon Pay, which aren't part of the typed shape) will show.
                const available = event.availablePaymentMethods !== undefined;
                setWalletsAvailable(available);
                if (!available) {
                  setCardExpanded(true);
                }
              }}
              onLoadError={() => {
                setWalletsAvailable(false);
                setCardExpanded(true);
              }}
              onConfirm={(event) => {
                void runConfirm(event);
              }}
            />
            {walletsAvailable === null ? (
              <PaymentLoader label="Loading payment options…" />
            ) : null}
          </Box>
        ) : null}

        {showDisclosure ? (
          <Accordion
            allowToggle
            index={cardExpanded ? [0] : []}
            onChange={(idx) => {
              const expanded = Array.isArray(idx) ? idx.length > 0 : idx >= 0;
              setCardExpanded(expanded);
            }}
          >
            <AccordionItem
              id={CARD_FORM_REGION_ID}
              border="1px solid"
              borderColor={
                cardExpanded ? "blaze.blaze" : "transparent.white.20"
              }
              borderRadius="16px"
              background="transparent.white.05"
              transition="border-color 150ms"
            >
              <AccordionButton
                gc-testing-id="btnExpandCard"
                px={4}
                py={3}
                _hover={{ background: "transparent.white.05" }}
                _expanded={{
                  borderBottomRadius: 0,
                }}
                borderRadius="16px"
              >
                <Flex flex={1} alignItems="center" gap={3}>
                  <CreditCardIcon
                    boxSize="20px"
                    color={cardExpanded ? "blaze.blaze" : "dune.600"}
                  />
                  <Text
                    fontWeight="500"
                    color={cardExpanded ? "blaze.blaze" : "dune.dune"}
                    fontSize="15px"
                  >
                    Card
                  </Text>
                </Flex>
                <AccordionIcon
                  color={cardExpanded ? "blaze.blaze" : "dune.600"}
                />
              </AccordionButton>
              <AccordionPanel
                px={4}
                pb={4}
                position="relative"
                minHeight={cardExpanded && !paymentReady ? "200px" : undefined}
              >
                <PaymentElement
                  options={PAYMENT_ELEMENT_OPTIONS}
                  onReady={() => setPaymentReady(true)}
                />
                {terms ? (
                  <Text
                    fontSize="13px"
                    color="dune.600"
                    mt={3}
                    lineHeight="140%"
                  >
                    {terms}
                  </Text>
                ) : null}
                {cardExpanded && !paymentReady ? (
                  <PaymentLoader label="Loading card form…" />
                ) : null}
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        ) : null}

        {error ? (
          <Text color="error.300" fontSize="14px" lineHeight="130%">
            {error}
          </Text>
        ) : null}

        {/* Internal submit is the default CTA. Callers that own an external
            primary CTA wired via formId pass hideInternalSubmitButton to
            avoid stacking two buttons. */}
        {cardExpanded ? (
          <Flex gap={3}>
            {onCancel ? (
              <Button
                type="button"
                variant="outline"
                flex={1}
                onClick={onCancel}
                isDisabled={submitting}
              >
                {cancelButtonText ?? "Back"}
              </Button>
            ) : null}
            {!hideInternalSubmitButton && (
              <Button
                gc-testing-id={submitTestId}
                type="submit"
                variant="primary"
                size="lg"
                flex={onCancel ? 2 : 1}
                isLoading={submitting}
                isDisabled={disabled || !paymentReady}
              >
                {submitButtonText}
              </Button>
            )}
          </Flex>
        ) : null}
      </VStack>
    </form>
  );
}

function PaymentLoader({ label }: { label: string }) {
  return (
    <Flex
      position="absolute"
      inset={0}
      justifyContent="center"
      alignItems="center"
      gap={3}
      aria-live="polite"
      aria-busy="true"
    >
      <Spinner size="sm" color="blaze.blaze" thickness="2px" />
      <Text color="dune.600" fontSize="14px">
        {label}
      </Text>
    </Flex>
  );
}
